import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Folder } from '@/types'
import apiService from '@/services/api'

interface FoldersState {
  tree: Folder[]
  byId: Record<number, Folder>
  isLoading: boolean
  error: string | null
  initialized: boolean
}

const initialState: FoldersState = {
  tree: [],
  byId: {},
  isLoading: false,
  error: null,
  initialized: false,
}

// Helper functions
const buildFolderMap = (folders: Folder[]): Record<number, Folder> => {
  const map: Record<number, Folder> = {}

  const addToMap = (folder: Folder) => {
    map[folder.id] = folder
    const children = folder.subfolders || folder.children || []
    children.forEach(addToMap)
  }

  folders.forEach(addToMap)
  return map
}

const updateFolderInTree = (tree: Folder[], updatedFolder: Folder): Folder[] => {
  return tree.map((folder) => {
    if (folder.id === updatedFolder.id) {
      return updatedFolder
    }
    const children = folder.subfolders || folder.children
    if (children && children.length > 0) {
      return {
        ...folder,
        subfolders: updateFolderInTree(children, updatedFolder),
        children: updateFolderInTree(children, updatedFolder),
      }
    }
    return folder
  })
}

const removeFolderFromTree = (tree: Folder[], folderId: number): Folder[] => {
  return tree
    .filter((folder) => folder.id !== folderId)
    .map((folder) => {
      const children = folder.subfolders || folder.children
      if (children && children.length > 0) {
        return {
          ...folder,
          subfolders: removeFolderFromTree(children, folderId),
          children: removeFolderFromTree(children, folderId),
        }
      }
      return folder
    })
}

const addFolderToTree = (tree: Folder[], newFolder: Folder, parentId?: number): Folder[] => {
  if (!parentId) {
    // Add to root level
    return [...tree, newFolder]
  }

  return tree.map((folder) => {
    if (folder.id === parentId) {
      const existingChildren = folder.subfolders || folder.children || []
      return {
        ...folder,
        subfolders: [...existingChildren, newFolder],
        children: [...existingChildren, newFolder],
      }
    }

    const children = folder.subfolders || folder.children
    if (children && children.length > 0) {
      return {
        ...folder,
        subfolders: addFolderToTree(children, newFolder, parentId),
        children: addFolderToTree(children, newFolder, parentId),
      }
    }

    return folder
  })
}

const moveFolderInTree = (tree: Folder[], folderId: number, newParentId?: number): Folder[] => {
  // First, find and remove the folder
  let folderToMove: Folder | null = null

  const findAndRemove = (folders: Folder[]): Folder[] => {
    return folders
      .filter((folder) => {
        if (folder.id === folderId) {
          folderToMove = folder
          return false
        }

        const children = folder.subfolders || folder.children
        if (children && children.length > 0) {
          const newChildren = findAndRemove(children)
          return {
            ...folder,
            subfolders: newChildren,
            children: newChildren,
          }
        }

        return true
      })
      .map((folder) => {
        const children = folder.subfolders || folder.children
        if (children && children.length > 0) {
          const newChildren = findAndRemove(children)
          return {
            ...folder,
            subfolders: newChildren,
            children: newChildren,
          }
        }
        return folder
      })
  }

  const treeWithoutFolder = findAndRemove(tree)

  if (!folderToMove) {
    return tree // Folder not found
  }

  // Then add it to the new location
  return addFolderToTree(treeWithoutFolder, folderToMove, newParentId)
}

// Async thunks
export const fetchFolderTree = createAsyncThunk(
  'folders/fetchTree',
  async (_, { rejectWithValue }) => {
    try {
      const tree = await apiService.getFolderTree()
      return tree
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to fetch folder tree'
      )
    }
  }
)

export const createFolder = createAsyncThunk(
  'folders/create',
  async (data: { name: string; description?: string; parent_id?: number }, { rejectWithValue }) => {
    try {
      const newFolder = await apiService.createFolder(data)
      return { folder: newFolder, parentId: data.parent_id }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to create folder'
      )
    }
  }
)

export const updateFolder = createAsyncThunk(
  'folders/update',
  async (
    { folderId, data }: { folderId: number; data: { name?: string; description?: string } },
    { rejectWithValue }
  ) => {
    try {
      const updatedFolder = await apiService.updateFolder(folderId, data)
      return { folder: updatedFolder, nameChanged: !!data.name }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to update folder'
      )
    }
  }
)

export const deleteFolder = createAsyncThunk(
  'folders/delete',
  async (folderId: number, { rejectWithValue }) => {
    try {
      await apiService.deleteFolder(folderId)
      return folderId
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to delete folder'
      )
    }
  }
)

export const moveFolder = createAsyncThunk(
  'folders/move',
  async (
    { folderId, targetParentId }: { folderId: number; targetParentId?: number },
    { rejectWithValue }
  ) => {
    try {
      await apiService.moveFolder(folderId, targetParentId)
      return { folderId, targetParentId }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || error.message || 'Failed to move folder'
      )
    }
  }
)

const foldersSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    resetState: (state) => {
      state.tree = []
      state.byId = {}
      state.isLoading = false
      state.error = null
      state.initialized = false
    },
    // Optimistic updates
    optimisticMoveFolder: (
      state,
      action: PayloadAction<{ folderId: number; targetParentId?: number }>
    ) => {
      const { folderId, targetParentId } = action.payload
      state.tree = moveFolderInTree(state.tree, folderId, targetParentId)
      state.byId = buildFolderMap(state.tree)
    },
    optimisticDeleteFolder: (state, action: PayloadAction<number>) => {
      const folderId = action.payload
      state.tree = removeFolderFromTree(state.tree, folderId)

      // Remove from byId map
      const removeFromMap = (id: number) => {
        const folder = state.byId[id]
        if (folder) {
          delete state.byId[id]
          const children = folder.subfolders || folder.children || []
          children.forEach((child) => removeFromMap(child.id))
        }
      }
      removeFromMap(folderId)
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch folder tree
      .addCase(fetchFolderTree.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchFolderTree.fulfilled, (state, action) => {
        state.isLoading = false
        state.initialized = true
        state.tree = action.payload
        state.byId = buildFolderMap(action.payload)
      })
      .addCase(fetchFolderTree.rejected, (state, action) => {
        state.isLoading = false
        state.initialized = true
        state.error = action.payload as string
      })

      // Create folder
      .addCase(createFolder.pending, (state) => {
        state.error = null
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        const { folder, parentId } = action.payload
        state.tree = addFolderToTree(state.tree, folder, parentId)
        state.byId[folder.id] = folder
      })
      .addCase(createFolder.rejected, (state, action) => {
        state.error = action.payload as string
      })

      // Update folder
      .addCase(updateFolder.pending, (state) => {
        state.error = null
      })
      .addCase(updateFolder.fulfilled, (state, action) => {
        const { folder } = action.payload

        // Always update the folder locally, including name changes
        state.tree = updateFolderInTree(state.tree, folder)
        state.byId[folder.id] = folder
      })
      .addCase(updateFolder.rejected, (state, action) => {
        state.error = action.payload as string
      })

      // Delete folder
      .addCase(deleteFolder.pending, (state) => {
        state.error = null
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        // Optimistic delete was already applied, no need to do anything
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.error = action.payload as string
        // TODO: Revert optimistic delete on error
      })

      // Move folder
      .addCase(moveFolder.pending, (state) => {
        state.error = null
      })
      .addCase(moveFolder.fulfilled, (state, action) => {
        // Optimistic move was already applied, no need to do anything
        // But we should refresh to get updated paths
      })
      .addCase(moveFolder.rejected, (state, action) => {
        state.error = action.payload as string
        // TODO: Revert optimistic move on error
      })
  },
})

export const { clearError, resetState, optimisticMoveFolder, optimisticDeleteFolder } =
  foldersSlice.actions
export default foldersSlice.reducer
